<template>
  <div class="entry-list-container">
    <div class="entry-list-toolbar px-2 pt-2">
      <input type="text" class="form-control entry-list-toolbar__search" placeholder="Buscar..." v-model="term">

      <div class="entry-list-actions">
        <div class="entry-list-chip">
          <i class="fa-solid fa-user"></i>
          <span>Inscritos: <strong>{{ count }}</strong></span>
        </div>

        <button class="btn btn-primary entry-list-actions__button" @click="goTonew">
          <i class="fa fa-plus-circle"></i>
          <span class="entry-list-actions__label entry-list-actions__label--desktop">Nueva entrada</span>
          <span class="entry-list-actions__label entry-list-actions__label--mobile">Añadir</span>
        </button>

        <button class="btn btn-success entry-list-actions__button" @click="exportExcel">
          <i class="fa fa-file-excel"></i>
          <span class="entry-list-actions__label entry-list-actions__label--desktop">Exportar Excel</span>
          <span class="entry-list-actions__label entry-list-actions__label--mobile">Excel</span>
        </button>
      </div>
    </div>

    <div class="entry-list-head">
      <span>Nombres</span>
      <span>Fecha inicio</span>
      <span>Fecha fin</span>
      <span>Monto</span>
      <span>Deuda</span>
      <span>Dias restantes</span>
    </div>

    <div v-if="entriesByTerm.length" class="entry-scrollarea">
      <Entry
        v-for="entry in entriesByTerm"
        :key="entry.id"
        :entry="entry"
      />
    </div>

    <div v-else class="entry-list-empty">
      <h3>Sin resultados</h3>
      <p>No encontramos registros con ese criterio. Puedes crear una nueva entrada o cambiar la busqueda.</p>
    </div>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue'
import { mapGetters } from 'vuex'
import * as XLSX from 'xlsx'

export default {
  components: {
    Entry: defineAsyncComponent(() => import('./Entry.vue'))
  },
  computed: {
    ...mapGetters('journal', ['getEntriesByTerm']),
    entriesByTerm() {
      return this.getEntriesByTerm(this.term)
    },
    count() {
      return this.getEntriesByTerm().length
    }
  },
  data() {
    return {
      term: ''
    }
  },
  methods: {
    goTonew() {
      this.$router.push({ name: 'entry', params: { id: 'new' } })
    },
    formatDate(value) {
      if (!value) return ''
      const date = value instanceof Date ? value : new Date(value)
      if (isNaN(date)) return String(value)
      return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`
    },
    exportExcel() {
      if (!this.entriesByTerm || this.entriesByTerm.length === 0) {
        alert('No hay datos para exportar.')
        return
      }

      const rows = this.entriesByTerm.map(entry => ({
        Nombre: entry.nombre || entry.name || '',
        'Fecha Inicio': this.formatDate(entry.fechaInicio || entry.fecha_inicio || entry.startDate),
        'Fecha Fin': this.formatDate(entry.fechaFin || entry.fecha_fin || entry.endDate),
        Monto: entry.monto ?? entry.amount ?? 0,
        Deuda: entry.deuda ?? entry.debt ?? 0,
        'Dias restantes': entry.diasRestantes ?? entry.dias_restantes ?? entry.daysLeft ?? 0
      }))

      const ws = XLSX.utils.json_to_sheet(rows)
      const cols = Object.keys(rows[0] || { tmp: '' }).map(key => ({
        wch: Math.max(key.length, ...rows.map(row => String(row[key] ?? '').length)) + 2
      }))
      ws['!cols'] = cols

      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Inscritos')

      const date = new Date()
      const file = `inscritos_${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}.xlsx`

      XLSX.writeFile(wb, file)
    }
  }
}
</script>

<style lang="scss" scoped>
.entry-list-container {
  --entry-grid-columns: minmax(160px, 2fr) repeat(5, minmax(90px, 1fr));
  --entry-grid-gap: 0.5rem;
  --entry-grid-padding-x: 0.75rem;
  border-right: 1px solid #2c3e50;
  height: calc(100vh - 56px);
  display: flex;
  flex-direction: column;
}

.entry-list-toolbar {
  display: grid;
  gap: 0.9rem;
}

.entry-list-toolbar__search {
  border-radius: 14px;
}

.entry-list-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
}

.entry-list-actions__button {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  border-radius: 10px;
  font-weight: 700;
  white-space: nowrap;
}

.entry-list-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.75rem 1rem;
  border-radius: 999px;
  background: #e8faff;
  color: #0b819b;
  font-weight: 700;
}

.entry-list-actions__label--mobile {
  display: none;
}

.entry-list-head {
  display: grid;
  grid-template-columns: var(--entry-grid-columns);
  gap: var(--entry-grid-gap);
  padding: 0.9rem var(--entry-grid-padding-x) 0.55rem;
  margin-top: 0.75rem;
  border-top: 1px solid #d8ebf1;
  border-bottom: 1px solid #d8ebf1;
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #4c6a79;
  text-align: center;
}

.entry-scrollarea {
  flex: 1;
  overflow: auto;
  padding-top: 0.75rem;
}

.entry-list-empty {
  margin: auto 1rem;
  padding: 1.5rem;
  border-radius: 18px;
  background: linear-gradient(180deg, #f6fcff 0%, #eef8fc 100%);
  text-align: center;
  color: #2c3e50;
}

.entry-list-empty h3 {
  margin-bottom: 0.5rem;
  font-size: 1.2rem;
}

.entry-list-empty p {
  margin: 0;
}

::-webkit-scrollbar {
  width: 10px;
}

::-webkit-scrollbar-thumb {
  background-color: #2c3e50;
}

@media (max-width: 900px) {
  .entry-list-container {
    border-right: 0;
  }

  .entry-list-toolbar {
    gap: 0.7rem;
  }

  .entry-list-actions {
    display: grid;
    grid-template-columns: minmax(0, 1.45fr) minmax(0, 1fr) auto;
    gap: 0.55rem;
    align-items: stretch;
  }

  .entry-list-chip,
  .entry-list-actions__button {
    min-height: 38px;
    padding: 0.6rem 0.75rem;
    justify-content: center;
    font-size: 0.92rem;
  }

  .entry-list-chip {
    border-radius: 8px;
    background: #324457;
    color: #fff;
    gap: 0.35rem;
  }

  .entry-list-actions__button {
    gap: 0.35rem;
  }

  .entry-list-actions__button.btn-primary {
    background: #324457;
    border-color: #324457;
  }

  .entry-list-actions__button.btn-success {
    padding-inline: 0.7rem;
  }

  .entry-list-actions__label--desktop {
    display: none;
  }

  .entry-list-actions__label--mobile {
    display: inline;
  }

  .entry-list-head {
    display: none;
  }

  .entry-scrollarea {
    padding-top: 0.35rem;
  }
}

@media (max-width: 430px) {
  .entry-list-toolbar {
    padding-right: 0.5rem;
    padding-left: 0.5rem;
  }

  .entry-list-actions {
    grid-template-columns: minmax(0, 1.55fr) minmax(0, 1fr) auto;
  }

  .entry-list-chip,
  .entry-list-actions__button {
    font-size: 0.84rem;
  }

  .entry-list-actions__button.btn-success {
    min-width: 42px;
  }

  .entry-list-actions__button.btn-success .entry-list-actions__label {
    display: none;
  }
}
</style>
